import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import CONFIG from '../config/config';
import { savePushSubscription, removePushSubscription, sendPushTest } from '../services/api';

// applicationServerKey must be a Uint8Array of the raw VAPID public key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

// Web push opt-in for THIS browser/device. Permission is only requested from
// enable() (a user gesture) — never on mount. `subscribed` reflects whether
// this device currently holds a push subscription.
export function usePushNotifications(userEmail) {
  const queryClient = useQueryClient();
  const supported = typeof window !== 'undefined'
    && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const [permission, setPermission] = useState(supported ? Notification.permission : 'unsupported');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Reflect this device's actual subscription state on mount
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => { if (!cancelled) setSubscribed(!!sub); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [supported]);

  const enable = useCallback(async () => {
    if (!supported || busy) return false;
    setBusy(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setError(perm === 'denied' ? 'Notifications are blocked for this site — enable them in your browser settings.' : 'Permission not granted.');
        return false;
      }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(CONFIG.VAPID_PUBLIC_KEY),
        });
      }
      await savePushSubscription(sub.toJSON());
      setSubscribed(true);
      queryClient.invalidateQueries(['pushDevices', userEmail || 'me']);
      return true;
    } catch (err) {
      console.error('[PUSH] enable failed:', err);
      setError(err.message || 'Failed to enable notifications');
      return false;
    } finally {
      setBusy(false);
    }
  }, [supported, busy, queryClient, userEmail]);

  const disable = useCallback(async () => {
    if (!supported || busy) return;
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await removePushSubscription(sub.endpoint).catch(() => {}); // best-effort server cleanup
        await sub.unsubscribe();
      }
      setSubscribed(false);
      queryClient.invalidateQueries(['pushDevices', userEmail || 'me']);
    } catch (err) {
      console.error('[PUSH] disable failed:', err);
      setError(err.message || 'Failed to disable notifications');
    } finally {
      setBusy(false);
    }
  }, [supported, busy, queryClient, userEmail]);

  const sendTest = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      return await sendPushTest();
    } catch (err) {
      setError(err.message || 'Test failed');
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  return { supported, permission, subscribed, busy, error, enable, disable, sendTest };
}
